import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import { toast } from 'sonner';
import { 
  FileText, Save, Eye, Plus, Trash2, Upload, 
  Type, Image, Grid, Palette, Settings, RefreshCw,
  MoveVertical, AlignLeft, AlignCenter, AlignRight,
  GripVertical, Copy, Download
} from 'lucide-react';
import api from '../../lib/api';

const defaultTemplate = {
  id: '',
  name: 'New Template',
  page_size: 'A4',
  orientation: 'portrait',
  margins: { top: 40, right: 40, bottom: 40, left: 40 },
  header: {
    enabled: true,
    logo_url: '',
    company_name: 'SP SAM Property Sdn Bhd',
    title: 'BOOKING FORM',
    subtitle: '',
    background_color: '#4E50FF',
    text_color: '#FFFFFF',
    height: 80,
  },
  sections: [
    {
      id: 'buyer_info',
      name: 'Buyer Information',
      order: 1,
      enabled: true,
      fields: [
        { id: 'buyer_name', label: 'Full Name', source: 'form_data.buyer_name', width: 'full' },
        { id: 'buyer_ic', label: 'IC/Passport No.', source: 'form_data.buyer_ic', width: 'half' },
        { id: 'buyer_phone', label: 'Phone', source: 'form_data.buyer_phone', width: 'half' },
        { id: 'buyer_email', label: 'Email', source: 'form_data.buyer_email', width: 'full' },
      ],
    },
    {
      id: 'property_info',
      name: 'Property Information',
      order: 2,
      enabled: true,
      fields: [
        { id: 'property_name', label: 'Property', source: 'property.name', width: 'full' },
        { id: 'unit_number', label: 'Unit No.', source: 'unit.unit_number', width: 'half' },
        { id: 'unit_type', label: 'Type', source: 'unit.unit_type', width: 'half' },
        { id: 'price', label: 'Price (RM)', source: 'unit.price', width: 'half', format: 'currency' },
      ],
    },
    {
      id: 'agent_info',
      name: 'Agent Information',
      order: 3,
      enabled: true,
      fields: [
        { id: 'agent_name', label: 'Agent Name', source: 'agent.full_name', width: 'half' },
        { id: 'agent_code', label: 'Agent Code', source: 'agent.agent_code', width: 'half' },
      ],
    },
  ],
  signatures: {
    enabled: true,
    positions: [
      { id: 'buyer', label: 'Buyer Signature', x: 50, width: 200 },
      { id: 'agent', label: 'Agent Signature', x: 350, width: 200 },
    ],
  },
  footer: {
    enabled: true,
    text: 'This is a computer-generated document. No signature required.',
    show_date: true,
    show_page_number: true,
  },
  styles: {
    font_family: 'Helvetica',
    font_size: 10,
    heading_size: 12,
    line_height: 1.5,
    section_spacing: 20,
    primary_color: '#4E50FF',
    text_color: '#333333',
    border_color: '#E5E7EB',
  },
};

const AdminPdfTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(defaultTemplate);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [activeTab, setActiveTab] = useState('header');
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get('/pdf-templates');
      const data = res.data || [];
      setTemplates(data);
      if (data.length > 0) {
        setSelectedTemplate(data[0]);
        // Merge with defaultTemplate so legacy docs missing header/sections/etc still render
        const t = data[0];
        setEditingTemplate({
          ...defaultTemplate,
          ...t,
          header: { ...defaultTemplate.header, ...(t.header || {}) },
          styles: { ...defaultTemplate.styles, ...(t.styles || {}) },
          signatures: { ...defaultTemplate.signatures, ...(t.signatures || {}) },
          footer: { ...defaultTemplate.footer, ...(t.footer || {}) },
          sections: t.sections && t.sections.length ? t.sections : defaultTemplate.sections,
        });
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      // Use default template if none exist
      setEditingTemplate(defaultTemplate);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    // Merge with defaultTemplate to ensure legacy docs (missing header/sections/etc) render
    setEditingTemplate({
      ...defaultTemplate,
      ...template,
      header: { ...defaultTemplate.header, ...(template.header || {}) },
      styles: { ...defaultTemplate.styles, ...(template.styles || {}) },
      signatures: { ...defaultTemplate.signatures, ...(template.signatures || {}) },
      footer: { ...defaultTemplate.footer, ...(template.footer || {}) },
      sections: template.sections && template.sections.length ? template.sections : defaultTemplate.sections,
    });
    setPreviewMode(false);
  };

  const handleCreateNew = () => {
    const newTemplate = {
      ...defaultTemplate,
      id: '',
      name: `New Template ${templates.length + 1}`,
    };
    setSelectedTemplate(null);
    setEditingTemplate(newTemplate);
    setPreviewMode(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingTemplate.id) {
        await api.put(`/pdf-templates/${editingTemplate.id}`, editingTemplate);
        toast.success('Template updated successfully');
      } else {
        const res = await api.post('/pdf-templates', editingTemplate);
        setEditingTemplate({ ...editingTemplate, id: res.data.id });
        toast.success('Template created successfully');
      }
      await loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingTemplate.id) return;
    
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await api.delete(`/pdf-templates/${editingTemplate.id}`);
      toast.success('Template deleted');
      await loadTemplates();
      handleCreateNew();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      setEditingTemplate(prev => ({
        ...prev,
        header: { ...prev.header, logo_url: e.target.result }
      }));
    };
    reader.readAsDataURL(file);
  };

  const updateHeader = (key, value) => {
    setEditingTemplate(prev => ({
      ...prev,
      header: { ...prev.header, [key]: value }
    }));
  };

  const updateStyles = (key, value) => {
    setEditingTemplate(prev => ({
      ...prev,
      styles: { ...prev.styles, [key]: value }
    }));
  };

  const updateSection = (sectionId, updates) => {
    setEditingTemplate(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === sectionId ? { ...s, ...updates } : s
      )
    }));
  };

  const updateSectionField = (sectionId, fieldId, updates) => {
    setEditingTemplate(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === sectionId 
          ? { ...s, fields: s.fields.map(f => f.id === fieldId ? { ...f, ...updates } : f) }
          : s
      )
    }));
  };

  const addField = (sectionId) => {
    const newField = {
      id: `field_${Date.now()}`,
      label: 'New Field',
      source: '',
      width: 'half',
    };
    setEditingTemplate(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === sectionId 
          ? { ...s, fields: [...s.fields, newField] }
          : s
      )
    }));
  };

  const removeField = (sectionId, fieldId) => {
    setEditingTemplate(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === sectionId 
          ? { ...s, fields: s.fields.filter(f => f.id !== fieldId) }
          : s
      )
    }));
  };

  const addSection = () => {
    const newSection = {
      id: `section_${Date.now()}`,
      name: 'New Section',
      order: editingTemplate.sections.length + 1,
      enabled: true,
      fields: [],
    };
    setEditingTemplate(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
  };

  const removeSection = (sectionId) => {
    setEditingTemplate(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== sectionId)
    }));
  };

  return (
    <div className="space-y-6" data-testid="admin-pdf-templates">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="h-6 w-6 text-[#4E50FF]" />
            PDF Template Editor
          </h1>
          <p className="text-slate-500 mt-1">Design custom PDF templates for booking forms</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleCreateNew}>
            <Plus className="h-4 w-4 mr-1" />
            New Template
          </Button>
          <Button
            variant={previewMode ? 'default' : 'outline'}
            onClick={() => setPreviewMode(!previewMode)}
          >
            <Eye className="h-4 w-4 mr-1" />
            {previewMode ? 'Edit' : 'Preview'}
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-[#4E50FF] hover:bg-[#4E50FF]/90"
          >
            {saving ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Save
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Template List */}
        <Card className="border shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-700">Templates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {templates.map((template) => (
              <div
                key={template.id}
                onClick={() => handleSelectTemplate(template)}
                className={`p-3 rounded-lg cursor-pointer border transition-colors ${
                  selectedTemplate?.id === template.id
                    ? 'border-[#4E50FF] bg-[#4E50FF]/5'
                    : 'border-transparent hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <FileText className={`h-4 w-4 ${selectedTemplate?.id === template.id ? 'text-[#4E50FF]' : 'text-slate-400'}`} />
                  <span className="font-medium text-slate-900 text-sm">{template.name}</span>
                </div>
              </div>
            ))}
            {templates.length === 0 && !loading && (
              <p className="text-sm text-slate-500 text-center py-4">No templates yet</p>
            )}
          </CardContent>
        </Card>

        {/* Editor / Preview */}
        <div className="lg:col-span-3">
          {previewMode ? (
            <Card className="border shadow-sm">
              <CardHeader>
                <CardTitle className="text-base font-medium">Preview: {editingTemplate.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <PdfPreview template={editingTemplate} />
              </CardContent>
            </Card>
          ) : (
            <Card className="border shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <Input
                    value={editingTemplate.name}
                    onChange={(e) => setEditingTemplate(prev => ({ ...prev, name: e.target.value }))}
                    className="text-lg font-semibold border-0 p-0 h-auto focus-visible:ring-0"
                    placeholder="Template Name"
                  />
                  {editingTemplate.id && (
                    <Button variant="ghost" size="sm" onClick={handleDelete} className="text-red-500 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid grid-cols-5 w-full">
                    <TabsTrigger value="header" className="text-xs">
                      <Image className="h-3 w-3 mr-1" />
                      Header
                    </TabsTrigger>
                    <TabsTrigger value="sections" className="text-xs">
                      <Grid className="h-3 w-3 mr-1" />
                      Sections
                    </TabsTrigger>
                    <TabsTrigger value="signatures" className="text-xs">
                      <Type className="h-3 w-3 mr-1" />
                      Signatures
                    </TabsTrigger>
                    <TabsTrigger value="footer" className="text-xs">
                      <AlignCenter className="h-3 w-3 mr-1" />
                      Footer
                    </TabsTrigger>
                    <TabsTrigger value="styles" className="text-xs">
                      <Palette className="h-3 w-3 mr-1" />
                      Styles
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="header" className="mt-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>Enable Header</Label>
                      <Switch
                        checked={editingTemplate.header.enabled}
                        onCheckedChange={(v) => updateHeader('enabled', v)}
                      />
                    </div>

                    {editingTemplate.header.enabled && (
                      <>
                        <div>
                          <Label>Logo</Label>
                          <div className="mt-2 flex items-center gap-4">
                            {editingTemplate.header.logo_url ? (
                              <img
                                src={editingTemplate.header.logo_url}
                                alt="Logo"
                                className="h-16 w-auto object-contain border rounded"
                              />
                            ) : (
                              <div className="h-16 w-24 border-2 border-dashed rounded flex items-center justify-center text-slate-400">
                                <Image className="h-6 w-6" />
                              </div>
                            )}
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/*"
                              onChange={handleLogoUpload}
                              className="hidden"
                            />
                            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                              <Upload className="h-4 w-4 mr-1" />
                              Upload Logo
                            </Button>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Company Name</Label>
                            <Input
                              value={editingTemplate.header.company_name}
                              onChange={(e) => updateHeader('company_name', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label>Title</Label>
                            <Input
                              value={editingTemplate.header.title}
                              onChange={(e) => updateHeader('title', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Background Color</Label>
                            <div className="flex gap-2 mt-1">
                              <input
                                type="color"
                                value={editingTemplate.header.background_color}
                                onChange={(e) => updateHeader('background_color', e.target.value)}
                                className="h-10 w-14 rounded border cursor-pointer"
                              />
                              <Input
                                value={editingTemplate.header.background_color}
                                onChange={(e) => updateHeader('background_color', e.target.value)}
                                className="flex-1"
                              />
                            </div>
                          </div>
                          <div>
                            <Label>Text Color</Label>
                            <div className="flex gap-2 mt-1">
                              <input
                                type="color"
                                value={editingTemplate.header.text_color}
                                onChange={(e) => updateHeader('text_color', e.target.value)}
                                className="h-10 w-14 rounded border cursor-pointer"
                              />
                              <Input
                                value={editingTemplate.header.text_color}
                                onChange={(e) => updateHeader('text_color', e.target.value)}
                                className="flex-1"
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </TabsContent>

                  <TabsContent value="sections" className="mt-4 space-y-4">
                    {editingTemplate.sections.map((section, sIndex) => (
                      <Card key={section.id} className="border">
                        <CardHeader className="py-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <GripVertical className="h-4 w-4 text-slate-400 cursor-grab" />
                              <Input
                                value={section.name}
                                onChange={(e) => updateSection(section.id, { name: e.target.value })}
                                className="w-48 h-8 text-sm font-medium"
                              />
                              <Switch
                                checked={section.enabled}
                                onCheckedChange={(v) => updateSection(section.id, { enabled: v })}
                              />
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => addField(section.id)}>
                                <Plus className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => removeSection(section.id)} className="text-red-500">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        {section.enabled && (
                          <CardContent className="pt-0 space-y-2">
                            {section.fields.map((field) => (
                              <div key={field.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                                <GripVertical className="h-4 w-4 text-slate-400 cursor-grab" />
                                <Input
                                  value={field.label}
                                  onChange={(e) => updateSectionField(section.id, field.id, { label: e.target.value })}
                                  className="w-32 h-8 text-sm"
                                  placeholder="Label"
                                />
                                <Input
                                  value={field.source}
                                  onChange={(e) => updateSectionField(section.id, field.id, { source: e.target.value })}
                                  className="flex-1 h-8 text-sm font-mono"
                                  placeholder="Data source (e.g., form_data.buyer_name)"
                                />
                                <Select
                                  value={field.width}
                                  onValueChange={(v) => updateSectionField(section.id, field.id, { width: v })}
                                >
                                  <SelectTrigger className="w-24 h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="full">Full</SelectItem>
                                    <SelectItem value="half">Half</SelectItem>
                                    <SelectItem value="third">Third</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => removeField(section.id, field.id)}
                                  className="text-red-500 h-8 w-8 p-0"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                            {section.fields.length === 0 && (
                              <p className="text-sm text-slate-400 text-center py-4">No fields. Click + to add.</p>
                            )}
                          </CardContent>
                        )}
                      </Card>
                    ))}
                    <Button variant="outline" onClick={addSection} className="w-full">
                      <Plus className="h-4 w-4 mr-1" />
                      Add Section
                    </Button>
                  </TabsContent>

                  <TabsContent value="signatures" className="mt-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>Enable Signatures</Label>
                      <Switch
                        checked={editingTemplate.signatures.enabled}
                        onCheckedChange={(v) => setEditingTemplate(prev => ({
                          ...prev,
                          signatures: { ...prev.signatures, enabled: v }
                        }))}
                      />
                    </div>

                    {editingTemplate.signatures.enabled && (
                      <div className="space-y-3">
                        {editingTemplate.signatures.positions.map((sig, index) => (
                          <div key={sig.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded">
                            <Input
                              value={sig.label}
                              onChange={(e) => {
                                const newPositions = [...editingTemplate.signatures.positions];
                                newPositions[index] = { ...sig, label: e.target.value };
                                setEditingTemplate(prev => ({
                                  ...prev,
                                  signatures: { ...prev.signatures, positions: newPositions }
                                }));
                              }}
                              className="w-40 h-8"
                              placeholder="Label"
                            />
                            <div className="flex items-center gap-2">
                              <Label className="text-xs">Width:</Label>
                              <Input
                                type="number"
                                value={sig.width}
                                onChange={(e) => {
                                  const newPositions = [...editingTemplate.signatures.positions];
                                  newPositions[index] = { ...sig, width: parseInt(e.target.value) };
                                  setEditingTemplate(prev => ({
                                    ...prev,
                                    signatures: { ...prev.signatures, positions: newPositions }
                                  }));
                                }}
                                className="w-20 h-8"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="footer" className="mt-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>Enable Footer</Label>
                      <Switch
                        checked={editingTemplate.footer.enabled}
                        onCheckedChange={(v) => setEditingTemplate(prev => ({
                          ...prev,
                          footer: { ...prev.footer, enabled: v }
                        }))}
                      />
                    </div>

                    {editingTemplate.footer.enabled && (
                      <>
                        <div>
                          <Label>Footer Text</Label>
                          <Input
                            value={editingTemplate.footer.text}
                            onChange={(e) => setEditingTemplate(prev => ({
                              ...prev,
                              footer: { ...prev.footer, text: e.target.value }
                            }))}
                            className="mt-1"
                          />
                        </div>
                        <div className="flex gap-6">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={editingTemplate.footer.show_date}
                              onCheckedChange={(v) => setEditingTemplate(prev => ({
                                ...prev,
                                footer: { ...prev.footer, show_date: v }
                              }))}
                            />
                            <Label>Show Date</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={editingTemplate.footer.show_page_number}
                              onCheckedChange={(v) => setEditingTemplate(prev => ({
                                ...prev,
                                footer: { ...prev.footer, show_page_number: v }
                              }))}
                            />
                            <Label>Show Page Number</Label>
                          </div>
                        </div>
                      </>
                    )}
                  </TabsContent>

                  <TabsContent value="styles" className="mt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Font Family</Label>
                        <Select
                          value={editingTemplate.styles.font_family}
                          onValueChange={(v) => updateStyles('font_family', v)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Helvetica">Helvetica</SelectItem>
                            <SelectItem value="Times-Roman">Times Roman</SelectItem>
                            <SelectItem value="Courier">Courier</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Page Size</Label>
                        <Select
                          value={editingTemplate.page_size}
                          onValueChange={(v) => setEditingTemplate(prev => ({ ...prev, page_size: v }))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="A4">A4</SelectItem>
                            <SelectItem value="Letter">Letter</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Font Size: {editingTemplate.styles.font_size}pt</Label>
                        <Slider
                          value={[editingTemplate.styles.font_size]}
                          onValueChange={([v]) => updateStyles('font_size', v)}
                          min={8}
                          max={14}
                          step={1}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>Heading Size: {editingTemplate.styles.heading_size}pt</Label>
                        <Slider
                          value={[editingTemplate.styles.heading_size]}
                          onValueChange={([v]) => updateStyles('heading_size', v)}
                          min={10}
                          max={18}
                          step={1}
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Primary Color</Label>
                        <div className="flex gap-2 mt-1">
                          <input
                            type="color"
                            value={editingTemplate.styles.primary_color}
                            onChange={(e) => updateStyles('primary_color', e.target.value)}
                            className="h-10 w-14 rounded border cursor-pointer"
                          />
                          <Input
                            value={editingTemplate.styles.primary_color}
                            onChange={(e) => updateStyles('primary_color', e.target.value)}
                            className="flex-1"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Text Color</Label>
                        <div className="flex gap-2 mt-1">
                          <input
                            type="color"
                            value={editingTemplate.styles.text_color}
                            onChange={(e) => updateStyles('text_color', e.target.value)}
                            className="h-10 w-14 rounded border cursor-pointer"
                          />
                          <Input
                            value={editingTemplate.styles.text_color}
                            onChange={(e) => updateStyles('text_color', e.target.value)}
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

// PDF Preview Component
const PdfPreview = ({ template }) => {
  const sampleData = {
    form_data: {
      buyer_name: 'Ahmad bin Abdullah',
      buyer_ic: '901234-56-7890',
      buyer_phone: '+60 12-345 6789',
      buyer_email: 'ahmad@example.com',
    },
    property: {
      name: 'Skyline Residence',
      location: 'Kuala Lumpur',
    },
    unit: {
      unit_number: 'A-15-08',
      unit_type: 'Condominium',
      price: 850000,
    },
    agent: {
      full_name: 'Sarah Lee',
      agent_code: 'AG-1234',
    },
  };

  const getValue = (source) => {
    if (!source) return '-';
    const parts = source.split('.');
    let value = sampleData;
    for (const part of parts) {
      value = value?.[part];
    }
    if (typeof value === 'number') {
      return value.toLocaleString('en-MY');
    }
    return value || '-';
  };

  return (
    <div className="bg-white border rounded-lg shadow-inner overflow-hidden" style={{ aspectRatio: '1/1.414' }}>
      <div className="h-full flex flex-col text-xs" style={{ transform: 'scale(0.9)', transformOrigin: 'top left', width: '111%' }}>
        {/* Header */}
        {template?.header?.enabled && (
          <div
            className="p-4 flex items-center justify-between"
            style={{
              backgroundColor: template.header.background_color,
              color: template.header.text_color,
            }}
          >
            <div className="flex items-center gap-3">
              {template.header.logo_url && (
                <img src={template.header.logo_url} alt="Logo" className="h-10 w-auto" />
              )}
              <div>
                <div className="font-bold text-sm">{template.header.company_name}</div>
                {template.header.subtitle && <div className="text-xs opacity-80">{template.header.subtitle}</div>}
              </div>
            </div>
            <div className="text-right">
              <div className="font-bold text-lg">{template.header.title}</div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 p-4 space-y-4">
          {(template?.sections || []).filter(s => s.enabled).map((section) => (
            <div key={section.id}>
              <div
                className="font-bold mb-2 pb-1 border-b"
                style={{
                  color: template.styles?.primary_color,
                  fontSize: `${template.styles?.heading_size}px`,
                }}
              >
                {section.name}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {(section.fields || []).map((field) => (
                  <div
                    key={field.id}
                    className={field.width === 'full' ? 'col-span-2' : ''}
                  >
                    <div className="text-slate-500" style={{ fontSize: '9px' }}>{field.label}</div>
                    <div className="font-medium" style={{ fontSize: `${template.styles?.font_size}px` }}>
                      {getValue(field.source)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Signatures */}
          {template?.signatures?.enabled && (
            <div className="mt-6">
              <div
                className="font-bold mb-3"
                style={{ color: template.styles?.primary_color }}
              >
                Signatures
              </div>
              <div className="flex gap-8">
                {(template.signatures.positions || []).map((sig) => (
                  <div key={sig.id} className="flex-1">
                    <div
                      className="border-b-2 border-slate-300 mb-1"
                      style={{ height: '40px', width: `${Math.min(sig.width, 150)}px` }}
                    />
                    <div className="text-slate-500" style={{ fontSize: '9px' }}>{sig.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {template?.footer?.enabled && (
          <div className="p-3 border-t bg-slate-50 flex justify-between text-slate-500" style={{ fontSize: '8px' }}>
            <div>{template.footer.text}</div>
            <div className="flex gap-4">
              {template.footer.show_date && <span>Date: {new Date().toLocaleDateString()}</span>}
              {template.footer.show_page_number && <span>Page 1 of 1</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPdfTemplates;
